<template>
  <div class="effectifs-panel">
    <div v-if="!serviceId" class="empty-state">
      <p class="text-gray-500 text-sm text-center py-4">Sélectionnez un service pour afficher les effectifs.</p>
    </div>

    <div v-else-if="loading" class="loading-state text-center py-4 text-sm text-gray-500">
      Chargement des effectifs…
    </div>

    <div v-else>
      <!-- Totaux -->
      <div class="stats-row">
        <div class="stat-item">
          <div class="stat-value">{{ totalPrevus }}</div>
          <div class="stat-label">Total prévus</div>
        </div>
        <div class="stat-item">
          <div class="stat-value text-red">{{ totalAbsents }}</div>
          <div class="stat-label">Absents</div>
        </div>
        <div class="stat-item">
          <div class="stat-value text-green">{{ totalPresents }}</div>
          <div class="stat-label">Présents</div>
        </div>
      </div>

      <!-- Tableau par niveau -->
      <div class="table-wrapper">
        <table class="effectifs-table">
          <thead>
            <tr>
              <th>Niveau</th>
              <th>Standard</th>
              <th>Sans porc</th>
              <th>Gluten</th>
              <th>Lactose</th>
              <th>Végétariens</th>
              <th>Animateurs</th>
              <th v-if="!readonly">Pers./barquette</th>
              <th v-if="absenceData">Absents</th>
              <th v-if="absenceData">Présents</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in visibleRows" :key="row.level">
              <td class="level-cell">{{ levelLabel(row.level) }}</td>
              <td>
                <input
                  v-if="!readonly"
                  v-model.number="row.nb_standard_meals"
                  type="number"
                  min="0"
                  class="cell-input"
                />
                <span v-else>{{ row.nb_standard_meals }}</span>
              </td>
              <td>
                <input
                  v-if="!readonly"
                  v-model.number="row.nb_no_pork_meals"
                  type="number"
                  min="0"
                  class="cell-input"
                />
                <span v-else>{{ row.nb_no_pork_meals }}</span>
              </td>
              <td>
                <input
                  v-if="!readonly"
                  v-model.number="row.nb_gluten_free_meals"
                  type="number"
                  min="0"
                  class="cell-input"
                />
                <span v-else>{{ row.nb_gluten_free_meals }}</span>
              </td>
              <td>
                <input
                  v-if="!readonly"
                  v-model.number="row.nb_lactose_free_meals"
                  type="number"
                  min="0"
                  class="cell-input"
                />
                <span v-else>{{ row.nb_lactose_free_meals }}</span>
              </td>
              <td>
                <input
                  v-if="!readonly"
                  v-model.number="row.nb_vegetarian_meals"
                  type="number"
                  min="0"
                  class="cell-input"
                />
                <span v-else>{{ row.nb_vegetarian_meals }}</span>
              </td>
              <td>
                <input v-if="!readonly" v-model.number="row.nb_animators" type="number" min="0" class="cell-input" />
                <span v-else>{{ row.nb_animators }}</span>
              </td>
              <td v-if="!readonly">
                <input v-model.number="row.nb_persons_per_tray" type="number" min="1" class="cell-input" />
              </td>
              <td v-if="absenceData" class="text-red">{{ absentsForLevel(row.level) }}</td>
              <td v-if="absenceData" class="text-green">{{ presentsForLevel(row) }}</td>
            </tr>
            <tr v-if="rows.length === 0">
              <td colspan="9" class="text-center text-gray-500 py-3">Aucun niveau configuré pour ce service.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Bouton enregistrer (mode écriture uniquement) -->
      <div v-if="!readonly" class="save-section">
        <p v-if="saveError" class="error-msg">{{ saveError }}</p>
        <p v-if="saveSuccess" class="success-msg">✓ Effectifs enregistrés avec succès</p>
        <button @click="save" :disabled="saving" class="btn btn-primary">
          {{ saving ? "Enregistrement…" : "Enregistrer" }}
        </button>
        <button v-if="rows.length === 0" @click="initRows" class="btn btn-secondary">
          Initialiser les niveaux
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const LEVELS_BY_VARIANT = {
  SIMPLE: ["MATERNELLE", "ELEMENTAIRE", "COLLEGE", "LYCEE", "ENSEIGNANT"],
  PRIMAIRE: ["PS", "MS", "GS", "CP", "CE1", "CE2", "CM1", "CM2", "ANIM-MAT", "ANIM-ELE", "ENSEIGNANT"],
  SECONDAIRE: ["SIXIEME", "CINQUIEME", "QUATRIEME", "TROISIEME", "SECONDE", "PREMIERE", "TERMINALE", "ENSEIGNANT"],
}
const LEVEL_LABELS = {
  MATERNELLE: "Maternelle",
  ELEMENTAIRE: "Élémentaire",
  COLLEGE: "Collège",
  LYCEE: "Lycée",
  ENSEIGNANT: "Enseignant",
  PS: "PS (Petite Section)",
  MS: "MS (Moyenne Section)",
  GS: "GS (Grande Section)",
  CP: "CP",
  CE1: "CE1",
  CE2: "CE2",
  CM1: "CM1",
  CM2: "CM2",
  "ANIM-MAT": "Animateurs maternelle",
  "ANIM-ELE": "Animateurs élémentaire",
  SIXIEME: "6ème",
  CINQUIEME: "5ème",
  QUATRIEME: "4ème",
  TROISIEME: "3ème",
  SECONDE: "2nde",
  PREMIERE: "1ère",
  TERMINALE: "Terminale",
}
const EMPTY_ROW = (level, serviceId) => ({
  id: null,
  level,
  service: serviceId,
  nb_standard_meals: 0,
  nb_no_pork_meals: 0,
  nb_gluten_free_meals: 0,
  nb_lactose_free_meals: 0,
  nb_vegetarian_meals: 0,
  nb_animators: 0,
  nb_trays: 0,
  nb_persons_per_tray: 1,
})

export default {
  name: "EffectifsPanel",

  props: {
    canteenId: { type: Number, default: null },
    serviceId: { type: Number, default: null },
    date: { type: String, default: null },
    readonly: { type: Boolean, default: true },
    variant: { type: String, default: "SIMPLE" },
  },

  data() {
    return {
      rows: [],
      absenceData: null,
      absenceLevels: [],
      loading: false,
      saving: false,
      saveError: null,
      saveSuccess: false,
    }
  },

  computed: {
    currentLevels() {
      return LEVELS_BY_VARIANT[this.variant] || LEVELS_BY_VARIANT.SIMPLE
    },
    visibleRows() {
      if (!this.readonly) return this.rows
      return this.rows.filter((r) => (r.nb_standard_meals || 0) + this.absentsForLevel(r.level) > 0)
    },
    totalPrevus() {
      return this.visibleRows.reduce((s, r) => s + (r.nb_standard_meals || 0), 0)
    },
    totalAbsents() {
      return this.visibleRows.reduce((s, r) => s + this.absentsForLevel(r.level), 0)
    },
    totalPresents() {
      return this.visibleRows.reduce((s, r) => s + this.presentsForLevel(r), 0)
    },
  },

  watch: {
    serviceId(val) {
      if (val) this.load()
    },
    canteenId(val) {
      if (val && this.serviceId) this.load()
    },
    date(val) {
      if (val && this.serviceId) this.load()
    },
  },

  mounted() {
    if (this.serviceId) this.load()
  },

  methods: {
    levelLabel(level) {
      return LEVEL_LABELS[level] || level
    },

    normalizeLevel(l) {
      return {
        id: l.id ?? null,
        level: l.level,
        service: l.service ?? this.serviceId,
        nb_standard_meals: l.nb_standard_meals ?? l.nbStandardMeals ?? 0,
        nb_no_pork_meals: l.nb_no_pork_meals ?? l.nbNoPorkMeals ?? 0,
        nb_gluten_free_meals: l.nb_gluten_free_meals ?? l.nbGlutenFreeMeals ?? 0,
        nb_lactose_free_meals: l.nb_lactose_free_meals ?? l.nbLactoseFreeMeals ?? 0,
        nb_vegetarian_meals: l.nb_vegetarian_meals ?? l.nbVegetarianMeals ?? 0,
        nb_animators: l.nb_animators ?? l.nbAnimators ?? 0,
        nb_trays: l.nb_trays ?? l.nbTrays ?? 0,
        nb_persons_per_tray: l.nb_persons_per_tray ?? l.nbPersonsPerTray ?? 1,
      }
    },

    async load() {
      this.loading = true
      this.saveError = null
      try {
        const levels = await this.$store.dispatch("fetchServiceLevels", this.serviceId)
        this.rows = Array.isArray(levels) ? levels.map(this.normalizeLevel) : []

        // En mode écriture, initialiser automatiquement les lignes si vides
        if (!this.readonly && this.rows.length === 0) {
          this.initRows()
        }

        if (this.canteenId && this.date) {
          const absences = await this.$store.dispatch("fetchAbsences", {
            canteenId: this.canteenId,
            date: this.date,
          })
          this.absenceData = Array.isArray(absences) && absences.length > 0 ? absences[0] : null
          if (this.absenceData) {
            const absLevels = await this.$store.dispatch("fetchAbsenceLevels", this.absenceData.id)
            this.absenceLevels = Array.isArray(absLevels) ? absLevels.map(this.normalizeLevel) : []
          } else {
            this.absenceLevels = []
          }
        }
      } finally {
        this.loading = false
      }
    },

    absentsForLevel(level) {
      const found = this.absenceLevels.find((l) => l.level === level)
      return found ? found.nb_standard_meals : 0
    },

    presentsForLevel(row) {
      return Math.max(0, (row.nb_standard_meals || 0) - this.absentsForLevel(row.level))
    },

    initRows() {
      this.rows = this.currentLevels.map((level) => EMPTY_ROW(level, this.serviceId))
    },

    async save() {
      if (this.rows.length === 0) {
        this.saveError = "Initialisez d'abord les niveaux avant d'enregistrer."
        return
      }
      this.saving = true
      this.saveError = null
      this.saveSuccess = false
      try {
        for (const row of this.rows) {
          const payload = {
            service: this.serviceId,
            level: row.level,
            nb_standard_meals: row.nb_standard_meals,
            nb_no_pork_meals: row.nb_no_pork_meals,
            nb_gluten_free_meals: row.nb_gluten_free_meals,
            nb_lactose_free_meals: row.nb_lactose_free_meals,
            nb_vegetarian_meals: row.nb_vegetarian_meals,
            nb_animators: row.nb_animators,
            nb_trays: row.nb_trays,
            nb_persons_per_tray: row.nb_persons_per_tray,
          }
          if (row.id) {
            await this.$store.dispatch("updateServiceLevel", { id: row.id, payload })
          } else {
            const created = await this.$store.dispatch("createServiceLevel", { payload })
            row.id = created.id
          }
        }
        this.saveSuccess = true
        this.$emit("saved")
      } catch (e) {
        this.saveError = `Erreur lors de l'enregistrement : ${e.message || "vérifiez votre connexion."}`
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.effectifs-panel {
  width: 100%;
}

.stats-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.stat-item {
  flex: 1;
  text-align: center;
  background: #f9fafb;
  border-radius: 0.5rem;
  padding: 0.75rem;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
}

.stat-label {
  font-size: 0.75rem;
  color: #6b7280;
}

.text-red {
  color: #dc2626;
}

.text-green {
  color: #16a34a;
}

.table-wrapper {
  overflow-x: auto;
}

.effectifs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.effectifs-table th {
  background: #f3f4f6;
  padding: 0.5rem 0.75rem;
  text-align: center;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  white-space: nowrap;
}

.effectifs-table td {
  padding: 0.5rem 0.75rem;
  text-align: center;
  border-bottom: 1px solid #f3f4f6;
}

.level-cell {
  text-align: left;
  font-weight: 600;
  color: #374151;
}

.cell-input {
  width: 60px;
  text-align: center;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  padding: 0.25rem;
  font-size: 0.875rem;
}

.cell-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.save-section {
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: flex-end;
}

.error-msg {
  font-size: 0.875rem;
  color: #dc2626;
  flex: 1;
}

.success-msg {
  font-size: 0.875rem;
  color: #16a34a;
  font-weight: 600;
  flex: 1;
}

.btn {
  padding: 0.5rem 1.25rem;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:disabled {
  background: #93c5fd;
  cursor: not-allowed;
}

.btn-secondary {
  background: #e5e7eb;
  color: #374151;
}

.text-center {
  text-align: center;
}

.text-gray-500 {
  color: #6b7280;
}

.py-4 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.py-3 {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}
</style>
